import { FC, useEffect, useState } from "react";
import GridWrapper from "../../../../../components/controls/GridWrapper";
import { NumberInput, TextAreaInput, TextInput } from "../../../../../components/controls/inputs";
import { BaseModalWindow } from "../../../../../components/modalWindows/BaseModalWindow";
import modalStyles from "../../../../../components/modalWindows/styles/BaseModalWindow.module.scss";
import { ILotDTO } from "../../../../../libs/coreapi-dto/accounting/lot";
import { IMovementItemCreateDTO } from "../../../../../libs/coreapi-dto/documents/movementItem";
import { v4 as uuidv4 } from "uuid";
import { ICreateItem } from "../MovementUpdateForm";
import { KizBoxEntity } from "../../../../Dictionaries/Kiz/KizBoxEntity";
import { IKizBoxDTO, IKizDTO } from "../../../../../libs/coreapi-dto/dirs/kiz";
import { useAppContext } from "../../../../../system/providers/appContextProvider";
import { KizDataProvider } from "../../../../../Services/DataProviders/KizDataProvider";
import buffer from "buffer";
import { useLotRemain } from "../../../../../components/lotSelector/useLotRemain";
import useOperativeReserve from "../../../../../components/lotSelector/useOperativeReserve";
import { IdTableVariant } from "../../../../../@types/enumsGlobal";
import { useForm } from "../../../../../system/hooks/useForm";
import { DateTime } from "luxon";
import { KizParsed } from "../../../../Dictionaries/Kiz/KizParsed";
import { roundMoneyFormat } from "../../../../../system/functions/round";
import { useTranslation } from "react-i18next";
import { GoodsDataProvider } from "../../../../../Services/DataProviders/GoodsDataProvider";
import { IGoodsWtruCodeGroupDTO } from "../../../../../libs/coreapi-dto/dirs/goods";

interface IMovementCreateItemModal {
    idDocumentGlobal: string;
    idDocumentItemGlobal: string;
    lot: ILotDTO;
    ok: (createItem: ICreateItem) => void;
    scanData?: string;
    cancel: () => void;
    items: IMovementItemCreateDTO[];
    requiredQuantity?: number;
    isWithdrawalGismtForUnscannedLots: boolean
    title?: string
}

interface IValidator {
    quantity: number;
}

export const MovementCreatorItemModal: FC<IMovementCreateItemModal> = (props) => {
    const appCtx = useAppContext();
    const kizDataProvider = new KizDataProvider(appCtx.coreApiService);
    const goodsDataProvider = new GoodsDataProvider(appCtx.coreApiService);

    const [createItem, setCreateItem] = useState<IMovementItemCreateDTO>({} as IMovementItemCreateDTO);
    const [idGlobal, setIdGlobal] = useState<string>(props.idDocumentItemGlobal ?? uuidv4());

    const [actualRequiredQuantity, setActualRequiredQuantity] = useState<number>(1)
    const [kizGoodsGroup, setKizGoodsGroup] = useState<string>("")

    const isDivided = (props.lot.numerator ?? 1) < (props.lot.denominator ?? 1)
    const isBox = (props.lot.numerator ?? 1) > (props.lot.denominator ?? 1)
    const isKiz = props.lot.isKiz === true

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.movement." + value);

    useEffect(() => {
        if (props.scanData && (createItem?.kizs || createItem.kizBoxes )) {
            if (isValid()) {
                onOk();
            }
        }
    },[createItem])

    const getCountKiz = (data: IMovementItemCreateDTO) => {
        if (data?.kizs?.length !== 0) return data?.kizs?.length;
        if (data?.kizBoxes?.length !== 0) return data?.kizBoxes?.length;
        if (data?.kizOsuDtos?.length !== 0) return data?.kizOsuDtos?.length;
        if (data?.kizOsuDto) return 1;
        return 0;
    }

    useEffect(() => {
        if (props.scanData) {
            let kizParsed = new KizParsed(props.scanData) 
            let scanData = (props.scanData.length === 18) ? ('00' + props.scanData) : props.scanData;
            const scanDataBase64: string = buffer.Buffer.from(scanData, "ascii").toString("base64");
            if (KizBoxEntity.isKizBox(props.scanData)) {
                kizDataProvider.parse(scanDataBase64, (parsedBarcode) => {
                    setCreateItem({
                        idGlobal: idGlobal,
                        idGoodsGlobal: props.lot.goods.idGlobal,
                        idScalingRatioGlobal: props.lot.scalingRatio.idGlobal,
                        idLotFromGlobal: props.lot.idGlobal,
                        idGosContractGoodsGlobal: props.lot?.idGosContractGoodsExternalGlobal,
                        supplierPrice: props.lot.supplierCostInfo?.price ?? 0,
                        actualQuantity: 0,
                        quantity: 1,
                        goodsCode: props.lot.goodsCode,
                        producer: props.lot.producer.displayName,
                        supplierCostInfo: props.lot.supplierCostInfo,
                        retailCostInfo: props.lot.retailCostInfo,
                        producerPrice: props.lot.producerPrice,
                        denominator: props.lot.denominator,
                        numerator: props.lot.numerator,
                        gosContractName: props.lot.gosContractName,
                        isKiz: props.lot.isKiz,
                        kizs: [] as IKizDTO[],
                        kizBoxes: [{ ...parsedBarcode.kizBoxData, idGlobal: uuidv4(), quantity: props.lot.numerator ?? 1 }],
                        supplierFrom: props.lot.supplier,
                        supplierTo: "",
                        supplier: props.lot.supplier,
                        dateCreated: DateTime.now(),
                        isOsu: props.lot?.kizOsuDto?.barcode !== undefined,
                        kizOsuDto: props.lot?.kizOsuDto?.barcode !== undefined ? {...createItem?.kizOsuDto, idDocumentItem: idGlobal } : undefined,
                        gtin: props.lot.gtin,
                        isPieceAccountingKizOsu: props.lot.isPieceAccountingKizOsu
                    } as IMovementItemCreateDTO);
                });
            } else if (kizParsed.isKiz) {
                kizDataProvider.parse(scanDataBase64, (parsedBarcode) => {
                    setCreateItem({
                        idGlobal: idGlobal,
                        idGoodsGlobal: props.lot.goods.idGlobal,
                        idScalingRatioGlobal: props.lot.scalingRatio.idGlobal,
                        idLotFromGlobal: props.lot.idGlobal,
                        idGosContractGoodsGlobal: props.lot?.idGosContractGoodsExternalGlobal,
                        supplierPrice: props.lot.supplierCostInfo?.price ?? 0,
                        actualQuantity: 0,
                        quantity: 1,
                        goodsCode: props.lot.goodsCode,
                        producer: props.lot.producer.displayName,
                        supplierCostInfo: props.lot.supplierCostInfo,
                        retailCostInfo: props.lot.retailCostInfo,
                        denominator: props.lot.denominator,
                        numerator: props.lot.numerator,
                        gosContractName: props.lot.gosContractName,
                        producerPrice: props.lot.producerPrice,
                        isKiz: props.lot.isKiz,
                        kizs: props.lot.isPieceAccountingKizOsu ? [] as IKizDTO[] : [{ ...parsedBarcode.kizData, idGlobal: uuidv4() }],
                        kizOsuDtos: props.lot.isPieceAccountingKizOsu ? [{ ...(parsedBarcode.kizData ?? parsedBarcode.kizOsuData), idGlobal: uuidv4() }] : [] as IKizDTO[],
                        kizBoxes: [] as IKizBoxDTO[],
                        supplierFrom: props.lot.supplier,
                        supplier: props.lot.supplier,
                        dateCreated: DateTime.now(),
                        isOsu: props.lot?.kizOsuDto?.barcode !== undefined,
                        kizOsuDto: props.lot?.kizOsuDto?.barcode !== undefined ? {...createItem?.kizOsuDto, idDocumentItem: idGlobal } : undefined,
                        gtin: props.lot.gtin,
                        isPieceAccountingKizOsu: props.lot.isPieceAccountingKizOsu
                    } as IMovementItemCreateDTO);
                });
            }
            else {
                setCreateItem({
                    idGlobal: idGlobal,
                    idGoodsGlobal: props.lot.goods.idGlobal,
                    idScalingRatioGlobal: props.lot.scalingRatio.idGlobal,
                    idLotFromGlobal: props.lot.idGlobal,
                    idGosContractGoodsGlobal: props.lot?.idGosContractGoodsExternalGlobal,
                    supplierCostInfo: props.lot.supplierCostInfo,
                    retailCostInfo: props.lot.retailCostInfo,
                    actualQuantity: 0,
                    quantity: 1,
                    supplierVatPrice: props.lot.supplierCostInfo?.vat ?? 0,
                    producerPrice: props.lot.producerPrice,
                    salePrice: props.lot.retailCostInfo?.price ?? 0,
                    isKiz: props.lot.isKiz,
                    denominator: props.lot.denominator,
                    numerator: props.lot.numerator,
                    kizs: [] as IKizDTO[],
                    goodsCode: props.lot.goodsCode,
                    producer: props.lot.producer.displayName,
                    kizBoxes: [] as IKizBoxDTO[],
                    kizOsuDtos: [] as IKizDTO[],
                    supplierFrom: props.lot.supplier,
                    supplierTo: "",
                    gosContractName: props.lot.gosContractName,
                    supplier: props.lot.supplier,
                    dateCreated: DateTime.now(),
                    isOsu: props.lot?.kizOsuDto?.barcode !== undefined,
                    kizOsuDto: props.lot?.kizOsuDto?.barcode !== undefined ? {...props.lot?.kizOsuDto, idDocumentItem: idGlobal } : undefined,
                    gtin: props.lot.gtin,
                    isPieceAccountingKizOsu: props.lot.isPieceAccountingKizOsu
                } as IMovementItemCreateDTO);
            }
        } 
        else {
            setCreateItem({
                idGlobal: idGlobal,
                idGoodsGlobal: props.lot.goods.idGlobal,
                idScalingRatioGlobal: props.lot.scalingRatio.idGlobal,
                idLotFromGlobal: props.lot.idGlobal,
                idGosContractGoodsGlobal: props.lot?.idGosContractGoodsExternalGlobal,
                supplierCostInfo: props.lot.supplierCostInfo,
                retailCostInfo: props.lot.retailCostInfo,
                actualQuantity: 0,
                quantity: 1,
                goodsCode: props.lot.goodsCode,
                producer: props.lot.producer.displayName,
                supplierVatPrice: props.lot.supplierCostInfo?.vat ?? 0,
                producerPrice: props.lot.producerPrice,
                salePrice: props.lot.retailCostInfo?.price ?? 0,
                isKiz: props.lot.isKiz,
                denominator: props.lot.denominator,
                numerator: props.lot.numerator,
                kizs: [] as IKizDTO[],
                kizBoxes: [] as IKizBoxDTO[],
                supplierFrom: props.lot.supplier,
                supplierTo: "",
                supplier: props.lot.supplier,
                dateCreated: DateTime.now(),
                isOsu: props.lot?.kizOsuDto?.barcode !== undefined,
                kizOsuDto: props.lot?.kizOsuDto?.barcode !== undefined ? {...props.lot?.kizOsuDto, idDocumentItem: idGlobal } : undefined,
                gtin: props.lot.gtin,
                isPieceAccountingKizOsu: props.lot.isPieceAccountingKizOsu,
                gosContractName: props.lot.gosContractName,
            } as IMovementItemCreateDTO);
        }
    }, []);

    useEffect(() => {
        if (props.lot && props.lot.goods) {
            props.isWithdrawalGismtForUnscannedLots && !isDivided && !isBox && !isKiz &&
                goodsDataProvider.getWtruCodeGroup(props.lot.goods.idGlobal,
                    (wtruGoodsGroup: IGoodsWtruCodeGroupDTO) => {
                        if (wtruGoodsGroup) {
                            setKizGoodsGroup(wtruGoodsGroup.groupName);
                        }
                    },
                    () => {
                        setKizGoodsGroup("Не определена");
                    }
                );
        }
    }, [props.lot]);

    const buildDocumentItem = (item: IMovementItemCreateDTO | undefined): ICreateItem => {
        let result = item ?? createItem;
        return {
            item: {
                ...result,
                isKiz: props.isWithdrawalGismtForUnscannedLots ? true : result.isKiz,
                supplierCostInfo: {
                    ...props.lot.supplierCostInfo,
                    vat: props.lot.supplierCostInfo.vat,
                    sum: roundMoneyFormat(props.lot.supplierCostInfo.price * result.quantity),
                    vatSum: roundMoneyFormat(props.lot.supplierCostInfo.vatPrice * result.quantity),
                    sumIncVat: roundMoneyFormat(props.lot.supplierCostInfo.priceIncVat * result.quantity),
                },
                retailCostInfo: {
                    ...props.lot.retailCostInfo,
                    vat: props.lot.retailCostInfo.vat,
                    sum: roundMoneyFormat(props.lot.retailCostInfo.price * result.quantity),
                    vatSum: roundMoneyFormat(props.lot.retailCostInfo.vatPrice * result.quantity),
                    sumIncVat: roundMoneyFormat(props.lot.retailCostInfo.priceIncVat * result.quantity),
                },
                dateCreated: DateTime.now()
            },
            displayItem: {
                idGlobal: result.idGlobal,
                idLotFromGlobal: result.idLotFromGlobal,
                goodsName: props.lot.goods.displayName,
                scalingRatioDisplayName: props.lot.scalingRatio.displayName,
                actualQuantity: result.actualQuantity,
                quantity: result.quantity,
                gosContractName: props.lot.gosContractName,

                gosContractFundingSourseName: props.lot.gosContractFundingSourseName,
                goodsCode: props.lot.goodsCode,
                producer: props.lot.producer.displayName,
                supplierVat: props.lot.supplierCostInfo.vat,
                supplierPrice: props.lot.supplierCostInfo.price,
                supplierVatPrice: props.lot.supplierCostInfo.vatPrice,
                supplierPriceIncVat: props.lot.supplierCostInfo.priceIncVat,
                supplierSum: roundMoneyFormat(props.lot.supplierCostInfo.price * result.quantity),
                supplierVatSum: roundMoneyFormat(props.lot.supplierCostInfo.vatPrice * result.quantity),
                supplierSumIncVat: roundMoneyFormat(props.lot.supplierCostInfo.priceIncVat * result.quantity),
                supplierAdprice: props.lot.supplierCostInfo.adprice,

                retailVat: props.lot.retailCostInfo.vat,
                retailPrice: props.lot.retailCostInfo.price,
                retailVatPrice: props.lot.retailCostInfo.vatPrice,
                retailPriceIncVat: props.lot.retailCostInfo.priceIncVat,
                retailSum: roundMoneyFormat(props.lot.retailCostInfo.price * result.quantity),
                retailVatSum: roundMoneyFormat(props.lot.retailCostInfo.vatPrice * result.quantity),
                retailSumIncVat: roundMoneyFormat(props.lot.retailCostInfo.priceIncVat * result.quantity),
                retailAdprice: props.lot.retailCostInfo.adprice,

                isKiz: props.isWithdrawalGismtForUnscannedLots ? true : props.lot.isKizFlag,
                countKizScan: getCountKiz(result),
                denominator: props.lot.denominator,
                numerator: props.lot.numerator,
                supplier: props.lot.supplier,
                dateCreated: DateTime.now(),
                isOsu: result.isOsu,
                bestBefore: props.lot.bestBefore,
                seriesNumber: props.lot.seriesNumber,
                lotNameParent: props.lot.lotName
            },
            filter: {
                idItem: result.idGlobal,
                quantityMax: props.lot.quantityRem + props.lot.quantityRes,
                quantityMin: 0,
            },
        };
    };

    const remain = useLotRemain(props.lot.idGlobal, props.idDocumentGlobal);
    const operativeReserve = useOperativeReserve();

    const onOk = () => {
        let id = idGlobal;
        let quantity = createItem.quantity;
        let kizs = createItem.kizs;
        let kizBoxes = createItem.kizBoxes;
        let kizOsuDtos = createItem.kizOsuDtos;
        let newCreateItem: IMovementItemCreateDTO;

        let item = props.items.find((c) => c.idLotFromGlobal === createItem.idLotFromGlobal);
        if (item) {
            id = item.idGlobal;
            kizs = [...kizs ?? [], ...item.kizs ?? []]
            kizBoxes = [...kizBoxes ?? [], ...item.kizBoxes ?? []]
            kizOsuDtos = [...kizOsuDtos ?? [], ...item.kizOsuDtos ?? []]

            //Считаем какое количество получится если приплюсовать к прошлому quantity
            const quantityFinnaly = item.quantity + quantity;

            // Считаем общее количество ВСЕХ маркированных единиц
            const totalKizCount = (kizs?.length ?? 0) + (kizBoxes?.length ?? 0) + (kizOsuDtos?.length ?? 0);

            //Если не маркированная партия, + 1 quantity
            //Если кол-во в партии и кол-во кизов совпадает, плюсуем quantity + 1
            //Если количество больше отсканированных кизов, то quantity не трогаем
            if (totalKizCount === 0 || quantityFinnaly === totalKizCount) {
                quantity = quantityFinnaly;
            }
            else if (quantityFinnaly > totalKizCount) {
                quantity = item.quantity;
            }
         

            newCreateItem = {
                ...item,
                actualQuantity: props.lot.isKiz ? 0 : quantity,
                quantity: quantity,
                kizs: kizs,
                kizBoxes:kizBoxes,
                kizOsuDtos: kizOsuDtos
            } as IMovementItemCreateDTO;
        }

        operativeReserve.send(
            {
                idLot: props.lot.idGlobal,
                idTable: IdTableVariant.Movement,
                idDocument: props.idDocumentGlobal,
                idDocumentItem: id,
                quantity: quantity,
            },
            () => props.ok(buildDocumentItem(newCreateItem))
        );
       
    };

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            quantity: {
                required: {
                    value: createItem.quantity && createItem.quantity > 0 ? false : true,
                    message: "Значение должно быть больше 0",
                },
            },
        },
    });

    useEffect(() => {
        if (props.requiredQuantity) {
            let calculatedQuantity: number;
            if (props.requiredQuantity > remain) {
                calculatedQuantity = remain;
            } else {
                calculatedQuantity = props.requiredQuantity;
            }
            
            setActualRequiredQuantity(calculatedQuantity);
            
            setCreateItem(prev => ({
                ...prev,
                quantity: calculatedQuantity,
                actualQuantity: props.lot.isKiz ? 0 : calculatedQuantity
            }));
        }
    }, [props.requiredQuantity, remain]);

    return (
        props.scanData ? <></> :
        <BaseModalWindow
            header={props.title ?? "Введите количество"}
            ok={{
                onClick: () => {
                    if (isValid()) {
                        onOk();
                    }
                },
            }}
            cancel={{ onClick: props.cancel }}
            modalWindowClassName={modalStyles.modalWindowMovement}
            footerStyle="confirm"
        >
            <TextAreaInput value={props.lot.displayName} disabled />
            <GridWrapper cols={3}>
                <TextInput label="Единица измерения" value={props.lot.scalingRatio.displayName} disabled />
                <NumberInput label="Остаток" value={remain} disabled />
                <NumberInput
                    required
                    onFocus={() => setErrors({ ...errors, quantity: undefined })}
                    error={errors.quantity}
                    keyBan={true}
                    label="Количество"
                    value={props.requiredQuantity ? actualRequiredQuantity : createItem.quantity}
                    min={1}
                    max={remain}
                    onChange={(value) => {
                        if (props.requiredQuantity) {
                            const validValue = Math.min(value, remain || value);
                            setActualRequiredQuantity(validValue);
                            setCreateItem({ 
                                ...createItem, 
                                quantity: validValue, 
                                actualQuantity: props.lot.isKiz ? 0 : validValue 
                            });
                        } else {
                            setCreateItem({ 
                                ...createItem, 
                                quantity: value, 
                                actualQuantity: props.lot.isKiz ? 0 : value 
                            });
                        }
                    }}
                />
            </GridWrapper>
            {
                props.isWithdrawalGismtForUnscannedLots && !isDivided && !isBox && !isKiz &&
                <TextAreaInput
                    label={baseT("kizGoodsGroup") + ":"}
                    value={kizGoodsGroup}
                    lineTextArea
                    disabled
                />
            }
        </BaseModalWindow>
    );
};
